import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Typography, Box, Paper, CssBaseline, Drawer, IconButton, Collapse, useMediaQuery, useTheme, Divider } from '@mui/material';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import ComplianceGauge from '../components/ComplianceGauge';
import BusinessIcon from '@mui/icons-material/Business';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PlaceIcon from '@mui/icons-material/Place';
import SensorsIcon from '@mui/icons-material/Sensors';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';

const SIDEBAR_WIDTH = 240;
const RIGHT_PANEL_WIDTH = 188;

/* ───── Hierarchy tree node ───── */
const TreeNode = ({ icon, label, children, level = 0, selected, onClick, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const hasChildren = children && React.Children.count(children) > 0;

  return (
    <Box>
      <Box
        onClick={() => { if (hasChildren) setOpen(o => !o); if (onClick) onClick(); }}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          pl: level * 1.5 + 1, pr: 1, py: 0.5,
          cursor: 'pointer', borderRadius: 1,
          bgcolor: selected ? 'rgba(76,175,80,0.18)' : 'transparent',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
          transition: 'background 0.15s',
        }}
      >
        {hasChildren ? (open ? <ExpandMoreIcon sx={{ fontSize: 16, color: '#888' }} /> : <ChevronRightIcon sx={{ fontSize: 16, color: '#888' }} />) : <Box sx={{ width: 16 }} />}
        {icon}
        <Typography noWrap variant="body2" sx={{ color: selected ? '#4CAF50' : '#ccc', fontWeight: selected ? 600 : 400, fontSize: 13 }}>
          {label}
        </Typography>
      </Box>
      {hasChildren && <Collapse in={open} timeout="auto">{children}</Collapse>}
    </Box>
  );
};

/* ───── build hierarchy tree from allSensors ───── */
const buildHierarchy = (allSensors) => {
  const tree = {};
  Object.entries(allSensors).forEach(([id, s]) => {
    const compName = s.company?.name || 'Unassigned';
    const compId = s.company?._id || '_none';
    const areaName = s.area?.name || 'Unassigned';
    const areaId = s.area?._id || '_none';
    const siteName = s.site?.name || 'Unassigned';
    const siteId = s.site?._id || '_none';
    if (!tree[compId]) tree[compId] = { name: compName, areas: {} };
    if (!tree[compId].areas[areaId]) tree[compId].areas[areaId] = { name: areaName, sites: {} };
    if (!tree[compId].areas[areaId].sites[siteId]) tree[compId].areas[areaId].sites[siteId] = { name: siteName, devices: [] };
    tree[compId].areas[areaId].sites[siteId].devices.push({ id, name: s.name || id });
  });
  // sort devices alphabetically within each site
  Object.values(tree).forEach(comp =>
    Object.values(comp.areas).forEach(area =>
      Object.values(area.sites).forEach(site =>
        site.devices.sort((a, b) => a.name.localeCompare(b.name))
      )
    )
  );
  return tree;
};

const IEQDashboard = ({ userData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [allSensors, setAllSensors] = useState({});
  const [selectedDevice, setSelectedDevice] = useState(null); // { id, name }
  const [sensorData, setSensorData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [timeSinceUpdate, setTimeSinceUpdate] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  // URL ?device=<id> 预选（从地图跳转而来）
  const urlDeviceRef = useRef(new URLSearchParams(window.location.search).get('device'));

  // ── 时间范围 & 统计粒度 ──
  const [selectedHours, setSelectedHours] = useState(24);
  const [granularity, setGranularity] = useState('raw');
  const [shownMetrics, setShownMetrics] = useState({ co2: true, temp: true, rh: true, pm25: true, hcho: false });

  // Fetch all sensor list for the hierarchy tree
  const fetchAllSensors = useCallback(async () => {
    try {
      const res = await fetch('/api/sensor/lastrecord');
      const data = await res.json();
      if (data.error) return;
      setAllSensors(data);
      // Auto-select: URL param 优先，否则取第一个
      if (!selectedDevice) {
        let targetEntry = null;
        const preSelect = urlDeviceRef.current;
        if (preSelect) {
          targetEntry = Object.entries(data).find(
            ([k, v]) => k === preSelect || v.name === preSelect
          );
          urlDeviceRef.current = null; // 只用一次
        }
        if (!targetEntry) {
          targetEntry = Object.entries(data).sort((a, b) => (a[1].name || '').localeCompare(b[1].name || ''))[0];
        }
        if (targetEntry) setSelectedDevice({ id: targetEntry[0], name: targetEntry[1].name || targetEntry[0] });
      }
    } catch (e) {
      console.error('Error fetching sensors:', e);
    }
  }, [selectedDevice]);

  // Parse selected device's data from allSensors
  useEffect(() => {
    if (!selectedDevice || !allSensors[selectedDevice.id]) { setSensorData(null); return; }
    const sd = allSensors[selectedDevice.id];
    const parsed = {
      name: sd.name || selectedDevice.id,
      deviceId: selectedDevice.id,
      scd: sd.SCD ? JSON.parse(sd.SCD) : null,
      sfa: sd.SFA ? JSON.parse(sd.SFA) : null,
      sps: sd.SPS ? JSON.parse(sd.SPS) : null,
      bmp: sd.BMP ? JSON.parse(sd.BMP) : null,
    };
    setSensorData(parsed);
    setLastUpdated(new Date(sd.timestamp).getTime());
  }, [selectedDevice, allSensors]);

  // Fetch history for selected device (window = selectedHours)
  const fetchHistory = useCallback(async () => {
    if (!selectedDevice) return;
    try {
      const now = Date.now();
      const start = now - selectedHours * 3600 * 1000;
      const url = `/api/sensor/history?repeator_id=${encodeURIComponent(selectedDevice.id)}&start_time=${start}&end_time=${now}`;
      const res = await fetch(url);
      const data = await res.json();
      setHistoryData(data);
    } catch (e) {
      console.error('Error:', e);
    }
  }, [selectedDevice, selectedHours]);

  useEffect(() => {
    fetchAllSensors();
    const iv = setInterval(fetchAllSensors, 5000);
    return () => clearInterval(iv);
  }, [fetchAllSensors]);

  useEffect(() => {
    if (!selectedDevice) return;
    setHistoryData(null);
    fetchHistory();
    const iv = setInterval(fetchHistory, 30000);
    return () => clearInterval(iv);
  }, [selectedDevice, fetchHistory]);

  useEffect(() => {
    const iv = setInterval(() => {
      setTimeSinceUpdate(Math.floor((Date.now() - lastUpdated) / 1000));
    }, 1000);
    return () => clearInterval(iv);
  }, [lastUpdated]);

  // Parse real-time values
  const co2 = sensorData?.scd?.['3'] ?? null;
  const tempSCD = sensorData?.scd?.['4'] != null ? sensorData.scd['4'] / 100 : null;
  const rhSCD = sensorData?.scd?.['5'] != null ? sensorData.scd['5'] / 100 : null;
  const pm25 = sensorData?.sps?.['1'] != null ? sensorData.sps['1'] / 100 : null;
  const pm10 = sensorData?.sps?.['3'] != null ? sensorData.sps['3'] / 100 : null;
  const hcho = sensorData?.sfa?.['3'] ?? null;

  // Compliance calc helpers
  const calcCompliance = (history, sensorKey, idx, divisor, min, max) => {
    if (!history || !history[sensorKey]) return null;
    const entries = Object.values(history[sensorKey]);
    if (entries.length === 0) return null;
    const good = entries.filter(e => {
      const v = e[idx] / divisor;
      return v >= min && v <= max;
    }).length;
    return Math.round((good / entries.length) * 100);
  };

  const thermalComp = calcCompliance(historyData, 'scd', 2, 100, 18, 28);
  const humidityComp = calcCompliance(historyData, 'scd', 3, 100, 30, 70);
  const co2Comp = historyData?.scd ? (() => {
    const entries = Object.values(historyData.scd);
    if (!entries.length) return null;
    const good = entries.filter(e => e[1] < 1000).length;
    return Math.round((good / entries.length) * 100);
  })() : null;
  const pmComp = historyData?.sps ? (() => {
    const entries = Object.values(historyData.sps);
    if (!entries.length) return null;
    const good = entries.filter(e => (e[1] / 100) < 25).length;
    return Math.round((good / entries.length) * 100);
  })() : null;

  // ── 按小时/按天聚合时间序列 ──
  const aggregateTimeSeries = (rawObj, gran) => {
    if (!rawObj) return null;
    const buckets = {};
    const getBucket = (ts) => {
      const d = new Date(isNaN(ts) ? ts : Number(ts));
      if (gran === 'hour') {
        const dd = new Date(d); dd.setMinutes(0, 0, 0); return dd.getTime();
      } else {
        const dd = new Date(d); dd.setHours(0, 0, 0, 0); return dd.getTime();
      }
    };
    Object.entries(rawObj).forEach(([ts, vals]) => {
      const bucket = getBucket(ts);
      if (!buckets[bucket]) buckets[bucket] = { count: 0, sums: null };
      buckets[bucket].count++;
      if (Array.isArray(vals)) {
        if (!buckets[bucket].sums) buckets[bucket].sums = vals.map(v => (typeof v === 'number' ? 0 : v));
        vals.forEach((v, i) => { if (typeof v === 'number') buckets[bucket].sums[i] += v; });
      }
    });
    const result = {};
    Object.entries(buckets).forEach(([bucket, { count, sums }]) => {
      if (sums) result[bucket] = sums.map(v => (typeof v === 'number' ? Math.round(v / count) : v));
    });
    return result;
  };

  // 聚合后的历史数据（用于图表显示）
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const processedHistory = useMemo(() => {
    if (!historyData || granularity === 'raw') return historyData;
    return {
      scd: aggregateTimeSeries(historyData.scd, granularity),
      sps: historyData.sps ? aggregateTimeSeries(historyData.sps, granularity) : null,
      sfa: historyData.sfa ? aggregateTimeSeries(historyData.sfa, granularity) : null,
    };
  }, [historyData, granularity]);

  // Build chart data
  const buildChart = () => {
    if (!processedHistory?.scd) return null;
    const times = Object.keys(processedHistory.scd).sort();
    const step = granularity === 'raw' ? Math.max(1, Math.floor(times.length / 200)) : 1;
    const sampled = times.filter((_, i) => i % step === 0);
    const fmtLabel = (t) => {
      const d = new Date(isNaN(t) ? t : Number(t));
      if (granularity === 'day') return `${d.getMonth() + 1}/${d.getDate()}`;
      if (granularity === 'hour') return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}h`;
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    };
    const labels = sampled.map(fmtLabel);
    const datasets = [];
    if (shownMetrics.temp) datasets.push({
      label: 'Temp [°C]',
      data: sampled.map(t => processedHistory.scd[t]?.[2] / 100),
      borderColor: '#FF6384', borderWidth: 1.5, pointRadius: granularity !== 'raw' ? 3 : 0, tension: 0.3, yAxisID: 'y',
    });
    if (shownMetrics.co2) datasets.push({
      label: 'CO₂ [ppm]',
      data: sampled.map(t => processedHistory.scd[t]?.[1]),
      borderColor: '#36A2EB', borderWidth: 1.5, pointRadius: granularity !== 'raw' ? 3 : 0, tension: 0.3, yAxisID: 'y1',
    });
    if (shownMetrics.rh) datasets.push({
      label: 'RH [%]',
      data: sampled.map(t => processedHistory.scd[t]?.[3] / 100),
      borderColor: '#4BC0C0', borderWidth: 1.5, pointRadius: granularity !== 'raw' ? 3 : 0, tension: 0.3, yAxisID: 'y',
    });
    if (shownMetrics.pm25 && processedHistory.sps) {
      const spsTimes = Object.keys(processedHistory.sps);
      datasets.push({
        label: 'PM2.5 [µg/m³]',
        data: sampled.map(t => {
          const closest = spsTimes.reduce((a, b) =>
            Math.abs(new Date(isNaN(b) ? b : Number(b)) - new Date(isNaN(t) ? t : Number(t)))
            < Math.abs(new Date(isNaN(a) ? a : Number(a)) - new Date(isNaN(t) ? t : Number(t))) ? b : a
          );
          return processedHistory.sps[closest]?.[1] / 100;
        }),
        borderColor: '#FFCE56', borderWidth: 1.5, pointRadius: granularity !== 'raw' ? 3 : 0, tension: 0.3, yAxisID: 'y',
      });
    }
    if (shownMetrics.hcho && processedHistory.sfa) {
      const sfaTimes = Object.keys(processedHistory.sfa);
      datasets.push({
        label: 'HCHO [ppb]',
        data: sampled.map(t => {
          const closest = sfaTimes.reduce((a, b) =>
            Math.abs(new Date(isNaN(b) ? b : Number(b)) - new Date(isNaN(t) ? t : Number(t)))
            < Math.abs(new Date(isNaN(a) ? a : Number(a)) - new Date(isNaN(t) ? t : Number(t))) ? b : a
          );
          return processedHistory.sfa[closest]?.[3];
        }),
        borderColor: '#B39DDB', borderWidth: 1.5, pointRadius: granularity !== 'raw' ? 3 : 0, tension: 0.3, yAxisID: 'y',
      });
    }
    return { labels, datasets };
  };

  const chartData = buildChart();
  const hierarchy = buildHierarchy(allSensors);

  const handleSelectDevice = (dev) => {
    setSelectedDevice(dev);
    if (isMobile) setSidebarOpen(false);
  };

  /* ───── Sidebar content ───── */
  const sidebarContent = (
    <Box sx={{ width: SIDEBAR_WIDTH, height: '100%', bgcolor: '#181818', borderRight: '1px solid #333', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #333' }}>
        <Typography variant="subtitle2" sx={{ color: '#aaa', letterSpacing: 1, textTransform: 'uppercase', fontSize: 12 }}>
          Devices
        </Typography>
        {isMobile && (
          <IconButton size="small" onClick={() => setSidebarOpen(false)} sx={{ color: '#888' }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
        {Object.entries(hierarchy).map(([compId, comp]) => (
          <TreeNode key={compId} icon={<BusinessIcon sx={{ fontSize: 16, color: '#90CAF9' }} />} label={comp.name} level={0} defaultOpen>
            {Object.entries(comp.areas).map(([areaId, area]) => (
              <TreeNode key={areaId} icon={<LocationCityIcon sx={{ fontSize: 16, color: '#CE93D8' }} />} label={area.name} level={1} defaultOpen>
                {Object.entries(area.sites).map(([siteId, site]) => (
                  <TreeNode key={siteId} icon={<PlaceIcon sx={{ fontSize: 16, color: '#FFB74D' }} />} label={site.name} level={2} defaultOpen>
                    {site.devices.map(dev => (
                      <TreeNode
                        key={dev.id}
                        icon={<SensorsIcon sx={{ fontSize: 14, color: selectedDevice?.id === dev.id ? '#4CAF50' : '#888' }} />}
                        label={dev.name}
                        level={3}
                        selected={selectedDevice?.id === dev.id}
                        onClick={() => handleSelectDevice(dev)}
                      />
                    ))}
                  </TreeNode>
                ))}
              </TreeNode>
            ))}
          </TreeNode>
        ))}
        {Object.keys(hierarchy).length === 0 && (
          <Typography variant="body2" sx={{ color: '#666', p: 2, textAlign: 'center' }}>Loading...</Typography>
        )}
      </Box>
    </Box>
  );

  /* ───── Main dashboard panel ───── */
  const dashboardPanel = (
    <Box sx={{ flex: 1, overflowY: 'auto', height: '100%', px: { xs: 1, md: 3 }, py: 2, pb: 10 }}>
      {!sensorData ? (
        <Box mt={4} textAlign="center">
          <Typography variant="h6" color="grey">
            {selectedDevice ? `Loading ${selectedDevice.name}…` : 'Select a device from the left panel'}
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 600 }}>
            {sensorData.name} — IEQ Dashboard
          </Typography>

          {/* REAL TIME AVERAGE */}
          <Paper elevation={3} sx={{ p: 2, mb: 2, bgcolor: '#1e1e1e', borderRadius: 2 }}>
            <Typography variant="subtitle2" align="center" sx={{ color: '#aaa', mb: 1, letterSpacing: 2, textTransform: 'uppercase' }}>
              Real Time Average
            </Typography>
            <Box display="flex" flexWrap="wrap" justifyContent="center" gap={2}>
              {co2 != null && (
                <Box textAlign="center" minWidth={80}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: co2 > 1000 ? '#f44336' : '#fff' }}>{co2}</Typography>
                  <Typography variant="caption" sx={{ color: '#999' }}>CO2 [ppm]</Typography>
                </Box>
              )}
              {tempSCD != null && (
                <Box textAlign="center" minWidth={80}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: (tempSCD > 28 || tempSCD < 18) ? '#f44336' : '#fff' }}>{tempSCD.toFixed(1)}°C</Typography>
                  <Typography variant="caption" sx={{ color: '#999' }}>Temperature</Typography>
                </Box>
              )}
              {rhSCD != null && (
                <Box textAlign="center" minWidth={80}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: (rhSCD > 70 || rhSCD < 30) ? '#f44336' : '#fff' }}>{rhSCD.toFixed(1)}%</Typography>
                  <Typography variant="caption" sx={{ color: '#999' }}>Humidity</Typography>
                </Box>
              )}
              {pm25 != null && (
                <Box textAlign="center" minWidth={80}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: pm25 > 25 ? '#f44336' : '#fff' }}>{pm25.toFixed(1)}</Typography>
                  <Typography variant="caption" sx={{ color: '#999' }}>PM2.5 [µg/m³]</Typography>
                </Box>
              )}
              {pm10 != null && (
                <Box textAlign="center" minWidth={80}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: pm10 > 50 ? '#f44336' : '#fff' }}>{pm10.toFixed(1)}</Typography>
                  <Typography variant="caption" sx={{ color: '#999' }}>PM10 [µg/m³]</Typography>
                </Box>
              )}
              {hcho != null && (
                <Box textAlign="center" minWidth={80}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: hcho > 200 ? '#f44336' : '#fff' }}>{hcho}</Typography>
                  <Typography variant="caption" sx={{ color: '#999' }}>HCHO [ppb]</Typography>
                </Box>
              )}
            </Box>
            <Typography variant="caption" align="center" display="block" sx={{ mt: 1, color: timeSinceUpdate > 605 ? '#f44336' : '#666' }}>
              Updated: {timeSinceUpdate}s ago | MAC: {sensorData.deviceId?.toUpperCase()}
            </Typography>
          </Paper>

          {/* COMPLIANCE GAUGES */}
          <Paper elevation={3} sx={{ p: 2, mb: 2, bgcolor: '#1e1e1e', borderRadius: 2 }}>
            <Typography variant="subtitle2" align="center" sx={{ color: '#aaa', mb: 1, letterSpacing: 2, textTransform: 'uppercase' }}>
              24h Compliance
            </Typography>
            <Box display="flex" justifyContent="center" flexWrap="wrap">
              <ComplianceGauge label="Thermal Comfort" value={thermalComp ?? 0} />
              <ComplianceGauge label="Humidity" value={humidityComp ?? 0} />
              <ComplianceGauge label="CO2" value={co2Comp ?? 0} />
              <ComplianceGauge label="Air Quality (PM2.5)" value={pmComp ?? 0} />
            </Box>
          </Paper>

          {/* HISTORY CHART */}
          <Paper elevation={3} sx={{ p: 2, mb: 2, bgcolor: '#1e1e1e', borderRadius: 2 }}>
            <Typography variant="subtitle2" align="center" sx={{ color: '#aaa', mb: 1, letterSpacing: 2, textTransform: 'uppercase' }}>
              {granularity === 'raw'
                ? `Recent History (${selectedHours}h)`
                : `${selectedHours}h 历史 — ${granularity === 'hour' ? '按小时均值' : '按天均值'}`}
            </Typography>
            {chartData ? (
              <Line
                data={chartData}
                height={window.innerWidth > 500 ? 150 : 250}
                options={{
                  responsive: true,
                  interaction: { mode: 'index', intersect: false },
                  plugins: { legend: { labels: { color: '#ccc', font: { size: 11 } } } },
                  scales: {
                    x: { ticks: { color: '#888', maxTicksLimit: 12, font: { size: 10 } }, grid: { color: '#333' } },
                    y: { position: 'left', ticks: { color: '#888', font: { size: 10 } }, grid: { color: '#333' }, title: { display: true, text: '°C / % / µg/m³', color: '#888' } },
                    y1: { position: 'right', ticks: { color: '#888', font: { size: 10 } }, grid: { drawOnChartArea: false }, title: { display: true, text: 'ppm', color: '#888' } },
                  },
                  elements: { point: { radius: 0 }, line: { borderWidth: 1.5, tension: 0.3 } },
                }}
              />
            ) : (
              <Typography align="center" color="grey">Loading chart...</Typography>
            )}
          </Paper>
        </>
      )}
    </Box>
  );

  /* ───── Right statistics panel ───── */
  const METRIC_LIST = [
    { key: 'co2',  label: 'CO₂',  color: '#36A2EB' },
    { key: 'temp', label: '温度',  color: '#FF6384' },
    { key: 'rh',   label: '湿度',  color: '#4BC0C0' },
    { key: 'pm25', label: 'PM2.5', color: '#FFCE56' },
    { key: 'hcho', label: '甲醒',  color: '#B39DDB' },
  ];
  const TIME_OPTIONS = [
    { label: '6 小时',  value: 6 },
    { label: '24 小时', value: 24 },
    { label: '3 天',   value: 72 },
    { label: '7 天',   value: 168 },
  ];
  const GRAN_OPTIONS = [
    { label: '原始数据', value: 'raw' },
    { label: '按小时',   value: 'hour' },
    { label: '按天',     value: 'day' },
  ];

  const rightPanel = (
    <Box sx={{
      width: RIGHT_PANEL_WIDTH, flexShrink: 0,
      height: '100%', bgcolor: '#181818',
      borderLeft: '1px solid #333',
      overflowY: 'auto', px: 1.5, py: 2,
      display: 'flex', flexDirection: 'column', gap: 1.5,
    }}>
      <Typography variant="subtitle2" sx={{ color: '#aaa', letterSpacing: 1, textTransform: 'uppercase', fontSize: 11 }}>
        统计设置
      </Typography>

      <Divider sx={{ borderColor: '#333' }} />

      {/* Time range selector */}
      <Box>
        <Typography variant="caption" sx={{ color: '#888', mb: 0.5, display: 'block', fontWeight: 600 }}>时间范围</Typography>
        {TIME_OPTIONS.map(opt => (
          <Box
            key={opt.value}
            onClick={() => setSelectedHours(opt.value)}
            sx={{
              py: 0.5, px: 1, borderRadius: 1, cursor: 'pointer', mb: 0.3,
              bgcolor: selectedHours === opt.value ? 'rgba(76,175,80,0.18)' : 'transparent',
              color: selectedHours === opt.value ? '#4CAF50' : '#ccc',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
              fontSize: 13, userSelect: 'none',
            }}
          >
            {selectedHours === opt.value ? '► ' : '  '}{opt.label}
          </Box>
        ))}
      </Box>

      <Divider sx={{ borderColor: '#333' }} />

      {/* Granularity selector */}
      <Box>
        <Typography variant="caption" sx={{ color: '#888', mb: 0.5, display: 'block', fontWeight: 600 }}>统计粒度</Typography>
        {GRAN_OPTIONS.map(opt => (
          <Box
            key={opt.value}
            onClick={() => setGranularity(opt.value)}
            sx={{
              py: 0.5, px: 1, borderRadius: 1, cursor: 'pointer', mb: 0.3,
              bgcolor: granularity === opt.value ? 'rgba(33,150,243,0.18)' : 'transparent',
              color: granularity === opt.value ? '#42A5F5' : '#ccc',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
              fontSize: 13, userSelect: 'none',
            }}
          >
            {granularity === opt.value ? '► ' : '  '}{opt.label}
          </Box>
        ))}
      </Box>

      <Divider sx={{ borderColor: '#333' }} />

      {/* Metric visibility */}
      <Box>
        <Typography variant="caption" sx={{ color: '#888', mb: 0.5, display: 'block', fontWeight: 600 }}>显示指标</Typography>
        {METRIC_LIST.map(m => (
          <Box
            key={m.key}
            onClick={() => setShownMetrics(prev => ({ ...prev, [m.key]: !prev[m.key] }))}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              py: 0.4, px: 1, borderRadius: 1, cursor: 'pointer', mb: 0.2,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
              userSelect: 'none',
            }}
          >
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: shownMetrics[m.key] ? m.color : '#444', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: shownMetrics[m.key] ? '#ccc' : '#555', fontSize: 12 }}>
              {m.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Per-bucket stats (hourly / daily) */}
      {granularity !== 'raw' && processedHistory?.scd && (
        <>
          <Divider sx={{ borderColor: '#333' }} />
          <Box>
            <Typography variant="caption" sx={{ color: '#aaa', mb: 0.5, display: 'block', fontWeight: 600 }}>
              {granularity === 'hour' ? '小时均值记录' : '日均值记录'}
            </Typography>
            {Object.entries(processedHistory.scd)
              .sort((a, b) => Number(a[0]) - Number(b[0]))
              .slice(-6)
              .map(([ts, vals]) => {
                const d = new Date(Number(ts));
                const label = granularity === 'hour'
                  ? `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:00`
                  : `${d.getMonth() + 1}/${d.getDate()}`;
                const co2v = vals[1];
                const tempv = vals[2] != null ? (vals[2] / 100).toFixed(1) : null;
                const rhv   = vals[3] != null ? (vals[3] / 100).toFixed(0) : null;
                return (
                  <Box key={ts} sx={{ mb: 0.8, pl: 0.5, borderLeft: '2px solid #333' }}>
                    <Typography sx={{ color: '#666', fontSize: 10 }}>{label}</Typography>
                    {co2v != null && <Typography sx={{ color: co2v > 1000 ? '#f44336' : '#81C784', fontSize: 11 }}>CO₂ {co2v} ppm</Typography>}
                    {tempv != null && <Typography sx={{ color: '#FFAB91', fontSize: 11 }}>温 {tempv}°C</Typography>}
                    {rhv  != null && <Typography sx={{ color: '#80DEEA', fontSize: 11 }}>RH {rhv}%</Typography>}
                  </Box>
                );
              })}
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <>
      <CssBaseline />
      <Box sx={{ display: 'flex', height: 'calc(100vh - 64px - 60px)', overflow: 'hidden' }}>
        {/* Mobile: Drawer sidebar */}
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            PaperProps={{ sx: { bgcolor: '#181818', top: 64, height: 'calc(100% - 64px - 60px)' } }}
          >
            {sidebarContent}
          </Drawer>
        ) : (
          /* Desktop: persistent sidebar */
          sidebarContent
        )}

        {/* Main content */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Toggle button on mobile */}
          {isMobile && (
            <Box sx={{ px: 1, pt: 1 }}>
              <IconButton size="small" onClick={() => setSidebarOpen(true)} sx={{ color: '#aaa' }}>
                <MenuIcon />
              </IconButton>
            </Box>
          )}
          {dashboardPanel}
        </Box>

        {/* Right statistics panel (desktop only) */}
        {!isMobile && rightPanel}
      </Box>
    </>
  );
};

export default IEQDashboard;
